import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Spinner, Text } from '@belong/ui';
import { useProperties } from 'accounts/hooks/homeowners/use-properties';
import { useActiveFullManagementHomes } from 'hooks/useActiveFullManagementHomes';
import { HomeServiceForm } from 'maintenance/components/home-service-form/home-service-form';
import { NavBar } from 'maintenance/components/nav-bar/nav-bar';
import { RecurringProServiceForm } from 'maintenance/components/recurring-pro-service-form/recurring-pro-service-form';
import { URL_QUERY_PARAMS } from 'maintenance/constants';
import { STRINGS } from 'maintenance/strings';
import type {
  EditProServiceRouteParams,
  RecurringProServicePreset,
} from 'maintenance/types';
import RenterAccountLease from 'models/renterAccounts/RenterAccountLease';
import { fetchRecurringProServicePresets } from 'store/redux/pros/actions';
import { selectRecurringProServicePresets } from 'store/redux/pros/selectors';
import { fetchResidentLeases } from 'store/redux/renter-accounts/actions';
import { selectLeases } from 'store/redux/renter-accounts/selectors';
import { formatSecondLine } from 'utils/formatAddress';
import {
  getLeaseStatus,
  LEASE_STATUS,
} from 'utils/formateDate/getTimeForLeasingStatus';

export function CreateProService() {
  const [isLoading, setIsLoading] = useState(false);
  const { location } = useHistory();
  const dispatch = useDispatch();
  const leases = useSelector<unknown, RenterAccountLease[]>(selectLeases);
  const { proService, taskId } = useParams<EditProServiceRouteParams>();
  const recurringProServicePresets = useSelector(
    selectRecurringProServicePresets,
  ) as RecurringProServicePreset[];
  const activeFullManagementHomes = useActiveFullManagementHomes();
  const { search } = useLocation();
  const externalUnitId = new URLSearchParams(search).get(
    URL_QUERY_PARAMS.UNIT_ID,
  );

  const isHomeOwnerView = location.pathname.includes('homeowner');

  const { properties = [] } = useProperties(isHomeOwnerView);

  const homeownerHome =
    externalUnitId && properties.length
      ? properties?.find((property) =>
          property.units.find(
            (unit: any) => unit.basicInfo.unitId === externalUnitId,
          ),
        )
      : null;

  const currentPreset = recurringProServicePresets.find(
    (preset) =>
      preset.recurringTask!.toLowerCase() === proService.toLowerCase(),
  );

  const hasClaimData = isHomeOwnerView ? !properties.length : !leases.length;

  const currentLease = leases?.filter(
    (lease) => getLeaseStatus(lease) === LEASE_STATUS.CURRENT,
  )?.[0];

  function getTitle() {
    if (currentPreset) return currentPreset?.summary;
    return taskId
      ? STRINGS['edit-request']
      : proService === 'Repair'
        ? STRINGS['create-repair']
        : STRINGS['create-upgrade'];
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await dispatch(fetchRecurringProServicePresets());

        if (!isHomeOwnerView) {
          await dispatch(fetchResidentLeases());
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch, isHomeOwnerView]);

  if (hasClaimData || isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const displayAddress = homeownerHome?.address || currentLease?.address;

  return (
    <main className="flex w-full flex-col">
      <NavBar isHomeOwnerView={isHomeOwnerView} />
      <div className="w-full self-center px-2sm sm:max-w-[730px] sm:px-0">
        <header className="mb-2xl mt-2sm text-center">
          <Text as="h1" fontWeight="semibold" variant="h2">
            {getTitle()}
          </Text>
          {displayAddress && (
            <Text as="p" variant="body" className="mt-xs">
              {displayAddress.streetAddress}, {formatSecondLine(displayAddress)}
            </Text>
          )}
        </header>
        {currentPreset ? (
          <RecurringProServiceForm
            isHomeOwnerView={isHomeOwnerView}
            preset={currentPreset}
            properties={activeFullManagementHomes}
            unit={currentLease?.unitInfo}
          />
        ) : (
          <HomeServiceForm
            isHomeOwnerView={isHomeOwnerView}
            properties={activeFullManagementHomes}
            unit={currentLease?.unitInfo}
            proService={proService}
          />
        )}
      </div>
    </main>
  );
}
